import { useMutation, useQuery } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { Connection } from '@domain/connection/models/Connection';
import { Connector } from '@domain/connection/models/Connector';
import { ClientType } from '@domain/connection/models/Connector';
import { BolEndpoint } from '@domain/connection/enums/endpoints.enum';
import { TERMINAL_BOL_SYNC_JOB_STATES } from '@domain/connection/models/BolSyncJob';
import { useFullSync } from '@features/connections/generic/hooks/useFullSync';

const POLL_INTERVAL_MS = 2000;

export const useBolConnector = () => {
    const { data: connector, isLoading, error } = useQuery<Connector | undefined>({
        queryKey: ['bol-connector'],
        queryFn: async () => {
            const result = await container.marketplaceRepository.fetchConnectors();
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data.find(connector => connector.type === ClientType.BOL);
        }
    });

    return {
        connector,
        isLoading,
        error
    };
};

export const useBolConnections = () => {
    const { data: connections, isLoading } = useQuery<Connection[]>({
        queryKey: ['bol-connections'],
        queryFn: async () => {
            const result = await container.marketplaceRepository.fetchConnections();
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data.filter(connection => connection.connector.type === ClientType.BOL);
        }
    });
    return {
        connections: connections || [],
        isLoading,
    };
};

const syncByEndpoint = (endpoint: BolEndpoint) => async (connectionId: string) => {
    const result = await container.bolConnectorRepository.syncBolMarketplaceConnection(connectionId, endpoint);
    if (result.status === 'error') throw new Error(result.error.message);
    return result.data;
};

export const useBolSync = () => {
    const { mutateAsync: syncAllOrders } = useMutation({
        mutationFn: syncByEndpoint(BolEndpoint.ORDERS_ALL),
    });

    const { mutateAsync: syncNewOrders } = useMutation({
        mutationFn: syncByEndpoint(BolEndpoint.ORDERS_NEW),
    });

    const { mutateAsync: syncOffers } = useMutation({
        mutationFn: syncByEndpoint(BolEndpoint.OFFERS),
    });

    const { mutateAsync: syncInventory } = useMutation({
        mutationFn: syncByEndpoint(BolEndpoint.INVENTORY),
    });

    const { mutateAsync: syncRanks } = useMutation({
        mutationFn: syncByEndpoint(BolEndpoint.RANKS),
    });

    const { mutateAsync: syncSearchTerms } = useMutation({
        mutationFn: syncByEndpoint(BolEndpoint.SEARCH_TERMS),
    });

    const { mutateAsync: syncSearchTerm } = useMutation({
        mutationFn: async ({ connectionId, keyword }: { connectionId: string; keyword: string }) => {
            const result = await container.bolConnectorRepository.syncSearchTerm(connectionId, keyword);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
    });

    const { mutateAsync: syncInvoices } = useMutation({
        mutationFn: syncByEndpoint(BolEndpoint.INVOICES),
    });

    const { mutateAsync: sync } = useMutation({
        mutationFn: async ({ connectionId, endpoint }: { connectionId: string; endpoint: BolEndpoint }) => {
            return syncByEndpoint(endpoint)(connectionId);
        },
    });

    const { triggerFullSync, isSyncing } = useFullSync('bol');

    const syncFull = (connection: Connection) =>
        triggerFullSync({
            connectionId: connection.id,
            connectionName: connection.name,
            syncFn: (connectionId) => container.bolConnectorRepository.syncFull(connectionId),
        });

    return {
        sync,
        syncAllOrders,
        syncNewOrders,
        syncOffers,
        syncInventory,
        syncRanks,
        syncSearchTerms,
        syncSearchTerm,
        syncInvoices,
        syncFull,
        isSyncing,
    };
};

export const useBolSyncJobStatus = (jobId: string | null) => {
    return useQuery({
        queryKey: ['bol-sync-job', jobId],
        queryFn: async () => {
            const response = await container.bolConnectorRepository.fetchBolJobStatus(jobId as string);
            if (response.status === 'error') throw new Error(response.error.message);
            return response.data;
        },
        enabled: !!jobId,
        refetchInterval: (data) => data && TERMINAL_BOL_SYNC_JOB_STATES.includes(data.state) ? false : POLL_INTERVAL_MS,
    });
};
